function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from 'react';
import { ApolloProvider, ApolloClient, HttpLink, InMemoryCache, disableFragmentWarnings, ApolloLink } from '@apollo/client';
import PropTypes from 'prop-types';
import { getClientTraceId, initializeTraceID } from '@thd-olt-functional/utils';
var typeDefs = "directive @client on FIELD\nextend type Media {\n    image(size: Int = 1000): Image\n}\n\nextend type Fulfillment {\n    isBODFS: Boolean\n    bodfs: Service\n    isBOPIS: Boolean\n    bopis: Service\n    isBOSS: Boolean\n    boss: Service\n    storeType: String\n    isCurrentlyUnavailable: Boolean\n    isOutOfStockOnline: Boolean\n    isSTH: Boolean\n    shipToHome: Service\n    isFulfillable: Boolean\n    method: String\n}\n\nextend type Identifiers {\n    isAppliance: Boolean\n}\n\nextend type AvailabilityType {\n    isFulfillableIRGSku: Boolean\n}\n\nextend type BaseProduct  {\n    isBrowseOnly: Boolean,\n    fulfillmentMethod: String\n}\n\nextend type FbrProduct {\n    isBrowseOnly: Boolean\n    fulfillmentMethod: String\n}\n\nextend type CollectionProduct {\n    isBrowseOnly: Boolean\n    fulfillmentMethod: String\n}\n\nextend type DynamicRecsProduct {\n    isBrowseOnly: Boolean\n    fulfillmentMethod: String\n}\n\nextend type EndCapProduct {\n    isBrowseOnly: Boolean\n    fulfillmentMethod: String\n}\n";
import { getRelativeAPIPath, dataIdFromObject, typePolicies, prepareQueryForCache } from "./util.js";
import possibleTypes from "../shapes/possibleTypes.js";
let client;
disableFragmentWarnings();
const cache = new InMemoryCache({
  possibleTypes,
  dataIdFromObject,
  typePolicies
});
if (typeof window !== 'undefined' && window.__APOLLO_STATE__) {
  //eslint-disable-line
  cache.restore(window.__APOLLO_STATE__); //eslint-disable-line
}
const DataProviderClient = _ref => {
  let {
    authHeaders,
    host,
    children,
    debug,
    headers,
    link,
    addTypename,
    clientOverride,
    credentials = 'include',
    federation = false
  } = _ref;
  // Useful for progressive hydration (using the same client in another tree).
  if (clientOverride) {
    return /*#__PURE__*/React.createElement(ApolloProvider, {
      client: clientOverride
    }, children);
  }
  // eslint-disable-next-line no-param-reassign
  headers = _objectSpread(_objectSpread({}, headers), {}, {
    'X-Parent-Trace-Id': window.THD_PARENT_TRACE_ID || ''
  });
  if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
    var _window$THDCustomer, _window$THDCustomer$d;
    window.THD_TRACE_ID = initializeTraceID();
    window.LIFE_CYCLE_EVENT_BUS.trigger('data-sources.trace', {
      parentTraceId: window.THD_PARENT_TRACE_ID,
      traceId: window.THD_TRACE_ID,
      mcvsId: (_window$THDCustomer = window.THDCustomer) === null || _window$THDCustomer === void 0 ? void 0 : (_window$THDCustomer$d = _window$THDCustomer.default) === null || _window$THDCustomer$d === void 0 ? void 0 : _window$THDCustomer$d.mcvisID
    });
  }
  const result = /env=(\w+)/.exec(window.location.search);
  const envParam = result && result[1];
  const linkOpts = {
    uri: getRelativeAPIPath({
      federation,
      host
    }),
    fetch: (uri, options) => {
      var _options$headers;
      const body = JSON.parse(options.body);
      const params = [`opname=${body.operationName}`];
      if (envParam) {
        params.push(`env=${envParam}`);
      }
      // eslint-disable-next-line
      options.headers['X-Cloud-Trace-Context'] = getClientTraceId();
      window.THD_TRACE_ID = (_options$headers = options.headers) === null || _options$headers === void 0 ? void 0 : _options$headers['X-Cloud-Trace-Context'];
      if ((options === null || options === void 0 ? void 0 : options.headers['X-current-url']) !== window.location.pathname) {
        // eslint-disable-next-line
        options.headers = (options === null || options === void 0 ? void 0 : options.headers) || {};
        // eslint-disable-next-line
        options.headers['X-current-url'] = window.location.pathname;
      }
      return fetch(`${uri}?${params.join('&')}`, options);
    },
    headers,
    credentials
  };
  const httpLink = new HttpLink(linkOpts);
  const apolloLink = link || httpLink;
  if (typeof addTypename !== 'undefined') {
    cache.addTypename = addTypename;
  }
  const authLink = new ApolloLink((operation, forward) => {
    const context = operation.getContext() || {};
    const customerToken = authHeaders === null || authHeaders === void 0 ? void 0 : authHeaders['x-thd-customer-token'];
    if (context.withAuth && customerToken) {
      operation.setContext(_objectSpread(_objectSpread({}, context), {}, {
        headers: _objectSpread(_objectSpread({}, context.headers), {}, {
          authorization: `Bearer ${customerToken}`
        })
      }));
    }
    return forward(operation);
  });
  const ssoLink = new ApolloLink((operation, forward) => {
    const context = operation.getContext() || {};
    const ssoToken = authHeaders === null || authHeaders === void 0 ? void 0 : authHeaders['x-thd-sso'];
    if (context.withSSO && ssoToken) {
      operation.setContext(_objectSpread(_objectSpread({}, context), {}, {
        headers: {
          'x-thd-sso': ssoToken
        }
      }));
    }
    return forward(operation);
  });
  client = new ApolloClient({
    cache,
    link: ApolloLink.from([authLink, ssoLink, apolloLink]),
    connectToDevTools: debug,
    typeDefs
  });
  if (debug && !window.__APOLLO_CLIENT__) {
    window.__APOLLO_CLIENT__ = client;
  }
  return /*#__PURE__*/React.createElement(ApolloProvider, {
    client: client
  }, children);
};
DataProviderClient.propTypes = {
  authHeaders: PropTypes.shape({
    'x-thd-customer-token': PropTypes.string,
    'x-thd-sso': PropTypes.string
  }),
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired,
  credentials: PropTypes.string,
  debug: PropTypes.bool,
  host: PropTypes.string,
  headers: PropTypes.shape({}),
  federation: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  link: PropTypes.any,
  addTypename: PropTypes.bool,
  clientOverride: PropTypes.shape({})
};
DataProviderClient.defaultProps = {
  authHeaders: null,
  credentials: 'include',
  debug: false,
  host: null,
  headers: {},
  federation: false,
  link: null,
  addTypename: true,
  clientOverride: null
};
DataProviderClient.persistCache = () => {
  return Promise.resolve({});
};
DataProviderClient.getClient = () => {
  return client;
};
DataProviderClient.writeQueryToCache = (queryInformation, options) => {
  if (!client) {
    console.log('no data provider client found');
    return;
  }
  client.writeQuery(prepareQueryForCache(queryInformation, options));
};
export default DataProviderClient;
//# sourceMappingURL=DataProvider.client.js.map